import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Recopilación de Información',
    body: 'Recopilamos información personal que nos proporcionas voluntariamente al realizar una compra, como tu nombre y dirección de correo electrónico. No recopilamos información adicional sin tu consentimiento.'
  }, {
    heading: 'Uso de la Información',
    body: 'Utilizamos tu información personal únicamente para procesar tu compra, enviarte el producto digital adquirido y comunicarnos contigo en caso de ser necesario respecto a tu pedido.'
  }, {
    heading: 'Protección de Datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Compartir Información',
    body: 'No vendemos, intercambiamos ni transferimos tu información personal a terceros. Esto no incluye terceros de confianza que nos ayudan a operar nuestro sitio web o procesar pagos, siempre que dichas partes acuerden mantener esta información confidencial.'
  }, {
    heading: 'Cookies',
    body: 'Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades del sitio.'
  }, {
    heading: 'Cambios a esta Política',
    body: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de la última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    body: 'El Excedente Fantasma es un producto digital educativo en formato de guía. Al completar tu compra, recibirás acceso inmediato al material. Este producto tiene fines educativos y no constituye asesoría financiera profesional.'
  }, {
    heading: 'Uso Personal',
    body: 'El contenido adquirido es para uso personal y no comercial. Queda prohibida la reproducción, distribución, modificación o reventa del material sin autorización previa por escrito.'
  }, {
    heading: 'Resultados',
    body: 'Los resultados pueden variar según la situación financiera individual de cada persona. No garantizamos resultados financieros específicos. La información proporcionada es de carácter educativo y orientativo.'
  }, {
    heading: 'Política de Reembolso',
    body: 'Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Si experimentas problemas técnicos para acceder al material, nos comprometemos a resolverlos.'
  }, {
    heading: 'Limitación de Responsabilidad',
    body: 'No nos hacemos responsables por decisiones financieras tomadas basándose en el contenido de esta guía. Recomendamos consultar con un asesor financiero profesional antes de tomar decisiones de inversión significativas.'
  }, {
    heading: 'Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos y condiciones en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const modalData = activeModal === 'privacy' ? privacyContent : activeModal === 'terms' ? termsContent : null;
  return <>
      <footer className="bg-navy-900 border-t border-navy-700/50 py-8 px-4 sm:px-6 lg:px-8" data-id="element-22">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8" data-id="element-23">
          <button onClick={() => setActiveModal('privacy')} className="text-sm text-slate-500 hover:text-gold-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900 rounded" data-id="element-24">
            Política de Privacidad
          </button>
          <span className="hidden sm:block w-1 h-1 rounded-full bg-slate-700" data-id="element-25" />
          <button onClick={() => setActiveModal('terms')} className="text-sm text-slate-500 hover:text-gold-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900 rounded" data-id="element-26">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-center text-xs text-slate-600 mt-4" data-id="element-27">
          © {new Date().getFullYear()} El Excedente Fantasma. Todos los
          derechos reservados.
        </p>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-28">
        {activeModal && modalData && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-29">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="bg-navy-800 border border-navy-700 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-30">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-navy-700" data-id="element-31">
                <h2 className="text-xl font-bold text-white font-heading" data-id="element-32">
                  {modalData.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-navy-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400" aria-label="Cerrar" data-id="element-33">
                  <X className="w-5 h-5" data-id="element-34" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-72px)] space-y-6" data-id="element-35">
                {modalData.sections.map((section, index) => <div key={index} data-id="element-36">
                    <h3 className="text-base font-bold text-gold-400 mb-2" data-id="element-37">
                      {section.heading}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed" data-id="element-38">
                      {section.body}
                    </p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}